// -----------------------------------------------------------
// FloraIQ - Situacao Atual do Exemplar (topo da FCA)
// -----------------------------------------------------------
// Resume, num so lugar, o que hoje esta espalhado pelas abas. Distingue tres
// estados: nao coletado, coletado mas nao validado (vistoria em andamento) e
// validado. So a vistoria VALIDADA define a condicao do exemplar.

import { Link } from "react-router-dom";
import type { TreeApi } from "@/lib/api";
import type { ArborInspectionDetail, ArborInspectionSummary } from "@/types/arboricultural-inspection";

interface Props {
  tree: TreeApi;
  inspections: ArborInspectionSummary[];
  validated: ArborInspectionDetail | null;
}

type Tone = "ok" | "warn" | "danger" | "pending" | "neutral";

const toneClasses: Record<Tone, string> = {
  ok: "bg-primary-50 border-primary-200 text-primary-800",
  warn: "bg-warning-50 border-warning-200 text-warning-800",
  danger: "bg-danger-50 border-danger-200 text-danger-800",
  pending: "bg-accent-50 border-accent-200 text-accent-800",
  neutral: "bg-background-50 border-background-200 text-foreground-700",
};

const REVIEW_LABELS: Record<string, { label: string; tone: Tone }> = {
  PENDENTE: { label: "Pendente de revisão", tone: "warn" },
  EM_REVISAO: { label: "Em revisão", tone: "pending" },
  APROVADO: { label: "Aprovado", tone: "ok" },
  VALIDADO: { label: "Validado", tone: "ok" },
  REJEITADO: { label: "Rejeitado", tone: "danger" },
};

const RISK_LABELS: Record<string, { label: string; tone: Tone }> = {
  BAIXO: { label: "Baixo", tone: "ok" },
  MEDIO: { label: "Médio", tone: "warn" },
  ALTO: { label: "Alto", tone: "danger" },
  CRITICO: { label: "Crítico", tone: "danger" },
};

const RECOMMENDATION_LABELS: Record<string, string> = {
  PODA_LIMPEZA: "Poda de limpeza",
  PODA_CORRETIVA: "Poda corretiva",
  PODA_EMERGENCIAL: "Poda emergencial",
  SUPRESSAO: "Supressão",
  TRANSPLANTE: "Transplante",
  TRATAMENTO_FITOSSANITARIO: "Tratamento fitossanitário",
  MONITORAR: "Monitorar",
  SEM_INTERVENCAO: "Sem intervenção",
  SUBSTITUICAO: "Substituição",
  PLANTIO_COMPLEMENTAR: "Plantio complementar",
};

const IN_PROGRESS = new Set(["DRAFT", "IN_PROGRESS", "SUBMITTED", "UNDER_REVIEW", "RETURNED"]);

const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString("pt-BR") : "";

const humanize = (code: string) => code.charAt(0) + code.slice(1).toLowerCase().replace(/_/g, " ");

interface Item {
  label: string;
  value: string;
  detail?: string;
  tone: Tone;
  icon: string;
  link?: { to: string; text: string };
}

export default function TreeCurrentSituation({ tree, inspections, validated }: Props) {
  const inProgress = inspections.find((i) => IN_PROGRESS.has(i.workflowStatus));
  const progressNote = inProgress ? `Vistoria em andamento desde ${fmtDate(inProgress.inspectionDate)}` : undefined;

  // 1. Situacao cadastral
  const review = REVIEW_LABELS[(tree.review_status ?? "").toUpperCase()] ?? {
    label: tree.review_status ? humanize(tree.review_status) : "Não informada",
    tone: "neutral" as Tone,
  };

  // 2. Identificacao botanica
  const botanical: Item = tree.species_id
    ? {
        label: "Identificação botânica",
        value: tree.species_popular_name ?? "Espécie informada",
        detail: tree.species_scientific_name ?? undefined,
        tone: "ok",
        icon: "ri-leaf-line",
        link: { to: `/especies/${tree.species_id}`, text: "Ver ficha botânica" },
      }
    : { label: "Identificação botânica", value: "Não confirmada", tone: "warn", icon: "ri-leaf-line" };

  // 3. Avaliacao de risco (so a validada vale)
  const ra = validated?.riskAssessment;
  const risk = ra?.riskClass ? RISK_LABELS[ra.riskClass.toUpperCase()] : undefined;
  const riskItem: Item = validated
    ? {
        label: "Avaliação de risco",
        value: risk ? `Risco ${risk.label}` : "Sem classe de risco",
        detail: [ra?.targetClass, `validada em ${fmtDate(validated.validatedAt ?? validated.inspectionDate)}`]
          .filter(Boolean)
          .join(" · "),
        tone: risk?.tone ?? "neutral",
        icon: "ri-alert-line",
      }
    : {
        label: "Avaliação de risco",
        value: "Não avaliado",
        detail: progressNote,
        tone: inProgress ? "pending" : "neutral",
        icon: "ri-alert-line",
      };

  // 4. Situacao de manejo
  const rec = validated?.recommendations?.[0];
  const priority = ra?.priorityClass;
  const manejo: Item = rec?.recommendationType
    ? {
        label: "Situação de manejo",
        value: RECOMMENDATION_LABELS[rec.recommendationType] ?? humanize(rec.recommendationType),
        detail: priority ? `Prioridade ${priority}${ra?.priorityDeadlineLabel ? ` — ${ra.priorityDeadlineLabel}` : ""}` : "Sem prioridade registrada",
        tone: rec.recommendationType === "SEM_INTERVENCAO" || rec.recommendationType === "MONITORAR" ? "ok" : "warn",
        icon: "ri-tools-line",
      }
    : { label: "Situação de manejo", value: "Sem recomendação", tone: "neutral", icon: "ri-tools-line" };

  // 5. Proxima vistoria: data sugerida pelo tecnico na vistoria validada; sem ela,
  // o prazo da prioridade P0-P4.
  const suggested = validated?.nextInspectionSuggestedAt;
  const dueIso = suggested ?? ra?.priorityDueAt ?? null;
  const due = dueIso ? new Date(dueIso) : null;
  const overdue = due ? due.getTime() < Date.now() : false;
  const next: Item = due
    ? {
        label: "Próxima vistoria",
        value: `${overdue ? "Vencida em" : "Até"} ${due.toLocaleDateString("pt-BR")}`,
        detail: suggested ? "Sugerida na última vistoria validada" : `Prazo da prioridade ${priority}`,
        tone: overdue ? "danger" : "pending",
        icon: "ri-calendar-event-line",
      }
    : {
        label: "Próxima vistoria",
        value: validated ? "Não programada" : "Primeira vistoria pendente",
        detail: validated ? undefined : progressNote,
        tone: validated ? "neutral" : "warn",
        icon: "ri-calendar-event-line",
      };

  const items: Item[] = [
    { label: "Situação cadastral", value: review.label, tone: review.tone, icon: "ri-file-list-3-line" },
    botanical,
    riskItem,
    manejo,
    next,
  ];

  return (
    <section aria-label="Situação atual do exemplar" className="bg-white rounded-card border border-background-200/70 p-4">
      <h2 className="text-sm font-semibold text-foreground-900 font-heading mb-3 flex items-center gap-2">
        <i className="ri-pulse-line text-primary-600"></i>
        Situação Atual do Exemplar
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2.5">
        {items.map((it) => (
          <div key={it.label} className={`rounded-lg border px-3 py-2.5 ${toneClasses[it.tone]}`}>
            <p className="text-[11px] font-medium opacity-80 flex items-center gap-1">
              <i className={`${it.icon} text-xs`}></i>
              {it.label}
            </p>
            <p className="text-sm font-semibold mt-0.5">{it.value}</p>
            {it.detail && <p className="text-[11px] opacity-80 mt-0.5">{it.detail}</p>}
            {it.link && (
              <Link to={it.link.to} className="text-[11px] font-medium underline underline-offset-2 mt-1 inline-flex items-center gap-1">
                <i className="ri-book-open-line"></i>
                {it.link.text}
              </Link>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
